import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Unplug, ExternalLink, RefreshCw, RotateCcw } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useLocationContext } from '@/components/layout/LocationLayout'
import {
  useIntegrationOverview,
  useTriggerSync,
  useUpdateCredential,
} from '@/hooks/useIntegrations'
import { ProviderIcon } from '@/components/integrations/ProviderIcon'
import { OverallHealthBadge } from '@/components/integrations/OverallHealthBadge'
import { HealthDot, HEALTH_LABELS } from '@/components/integrations/HealthDot'
import type { IntegrationOverviewCredential } from '@/types/integrations'
import { FlightAwareLocationCard } from './FlightAwareLocationCard'

export function LocationIntegrationsTab() {
  const { locationId } = useLocationContext()
  const navigate = useNavigate()
  const { data: overviewData, isLoading } = useIntegrationOverview()
  const triggerSync = useTriggerSync()
  const updateCredential = useUpdateCredential()

  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set())
  const [togglingCredentialId, setTogglingCredentialId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  // Collect all credential sets that have a credential for this location
  const locationIntegrations: { overview: typeof overviewData extends (infer T)[] ? T : never; credential: IntegrationOverviewCredential }[] = []
  for (const overview of overviewData ?? []) {
    for (const cred of overview.integration_credentials) {
      if (cred.location_id === locationId) {
        locationIntegrations.push({ overview, credential: cred })
      }
    }
  }

  const handleToggle = (credentialId: string, isActive: boolean) => {
    setTogglingCredentialId(credentialId)
    updateCredential.mutate(
      { id: credentialId, updates: { is_active: isActive } },
      { onSettled: () => setTogglingCredentialId(null) },
    )
  }

  const handleSync = (credentialId: string) => {
    setSyncingIds(prev => new Set(prev).add(credentialId))
    triggerSync.mutate(credentialId, {
      onSettled: () => {
        setSyncingIds(prev => {
          const next = new Set(prev)
          next.delete(credentialId)
          return next
        })
      },
    })
  }

  if (locationIntegrations.length === 0) {
    return (
      <div className="space-y-3">
        <FlightAwareLocationCard locationId={locationId} />
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Unplug className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">No other integrations connected</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Org admins can connect more integrations from Settings → Integrations.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <FlightAwareLocationCard locationId={locationId} />
      {locationIntegrations.map(({ overview, credential }) => {
        const provider = overview.integration_providers
        const providerName = provider?.name ?? overview.provider
        const providerType = provider?.provider_type ?? 'reservation'
        const isCircuitBroken = credential.health_status === 'circuit_broken'
        const isSyncing = syncingIds.has(credential.id)

        return (
          <Card key={credential.id}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <ProviderIcon providerType={providerType} className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-semibold">{providerName}</span>
                    <OverallHealthBadge credentials={[credential]} />
                  </div>

                  <div className="flex items-center gap-3 mt-1.5 ml-6">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1.5">
                            <HealthDot status={credential.health_status} />
                            <span className="text-xs text-muted-foreground">
                              {HEALTH_LABELS[credential.health_status]}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <p className="text-xs">
                            {credential.consecutive_failures > 0
                              ? `${credential.consecutive_failures} consecutive failure${credential.consecutive_failures !== 1 ? 's' : ''}`
                              : 'No recent errors'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {credential.last_successful_sync ? (
                      <span className="text-xs text-muted-foreground">
                        Last sync: {formatDistanceToNow(new Date(credential.last_successful_sync), { addSuffix: true })}
                      </span>
                    ) : credential.is_active ? (
                      <span className="text-xs text-muted-foreground">Never synced</span>
                    ) : null}
                  </div>

                  {credential.last_error && credential.consecutive_failures > 0 && (
                    <p className="text-xs text-destructive mt-1.5 ml-6 truncate max-w-[400px]">
                      {credential.last_error}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {isCircuitBroken ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      onClick={() => handleSync(credential.id)}
                      disabled={isSyncing}
                    >
                      {isSyncing ? (
                        <RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3 w-3 mr-1.5" />
                      )}
                      Reset & Retry
                    </Button>
                  ) : credential.is_active ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSync(credential.id)}
                      disabled={isSyncing}
                    >
                      <RefreshCw className={`h-3 w-3 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`} />
                      Sync Now
                    </Button>
                  ) : null}

                  <Switch
                    checked={credential.is_active}
                    onCheckedChange={(checked) => handleToggle(credential.id, checked)}
                    disabled={togglingCredentialId === credential.id || !overview.is_active}
                  />

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => navigate(`/settings/integrations/${overview.id}`)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
